import type { TrainingPlan, UserProfile } from '@shared/types';
import { logger } from '../utils/logger';
import { loadUserProfileSync } from '../utils/userProfile';
import apiClient from './api';
import { loadTrainingPlanSync } from './trainingPlanStorage';

export interface SyncResult {
  success: boolean;
  profileSynced: boolean;
  planSynced: boolean;
  errors: string[];
}

/**
 * Sync all local data to backend
 * Syncs user profile and training plan from localStorage to backend
 */
export async function syncToBackend(): Promise<SyncResult> {
  const result: SyncResult = {
    success: false,
    profileSynced: false,
    planSynced: false,
    errors: [],
  };

  logger.info('Starting sync to backend');

  // 1. Sync user profile
  try {
    const userProfile: UserProfile = loadUserProfileSync();
    if (userProfile) {
      logger.info('Syncing user profile to backend');
      const response = await apiClient.updateUserProfile(userProfile);

      if (response.success) {
        logger.info('User profile synced successfully');
        result.profileSynced = true;
      } else {
        const errorMsg = `Failed to sync profile: ${response.message || response.error}`;
        logger.warn(errorMsg);
        result.errors.push(errorMsg);
      }
    } else {
      logger.info('No user profile to sync');
    }
  } catch (error) {
    const errorMsg = `Error syncing profile: ${error instanceof Error ? error.message : 'Unknown error'}`;
    logger.error(errorMsg, error as Error);
    result.errors.push(errorMsg);
  }

  // 2. Sync training plan
  try {
    const trainingPlan: TrainingPlan | null = loadTrainingPlanSync();
    if (trainingPlan) {
      logger.info('Syncing training plan to backend');
      const response = await apiClient.updateStoredTrainingPlan(trainingPlan);

      if (response.success) {
        logger.info('Training plan synced successfully');
        result.planSynced = true;
      } else {
        const errorMsg = `Failed to sync plan: ${response.message || response.error}`;
        logger.warn(errorMsg);
        result.errors.push(errorMsg);
      }
    } else {
      logger.info('No training plan to sync');
    }
  } catch (error) {
    const errorMsg = `Error syncing plan: ${error instanceof Error ? error.message : 'Unknown error'}`;
    logger.error(errorMsg, error as Error);
    result.errors.push(errorMsg);
  }

  // Overall success if no errors occurred
  result.success = result.errors.length === 0;

  if (result.success) {
    logger.info('Sync completed successfully');
  } else {
    logger.warn('Sync completed with errors', { errors: result.errors });
  }

  return result;
}

/**
 * Sync from backend to local storage
 * Loads user profile and training plan from backend and updates localStorage
 */
export async function syncFromBackend(): Promise<SyncResult> {
  const result: SyncResult = {
    success: false,
    profileSynced: false,
    planSynced: false,
    errors: [],
  };

  logger.info('Starting sync from backend');

  // 1. Sync user profile
  try {
    logger.info('Loading user profile from backend');
    const response = await apiClient.getUserProfile();

    if (response.success && response.data) {
      // Save to localStorage
      localStorage.setItem('pacepilot_user_profile', JSON.stringify(response.data));
      logger.info('User profile synced from backend');
      result.profileSynced = true;
    } else {
      const errorMsg = `Failed to load profile: ${response.message || response.error}`;
      logger.warn(errorMsg);
      result.errors.push(errorMsg);
    }
  } catch (error) {
    const errorMsg = `Error loading profile: ${error instanceof Error ? error.message : 'Unknown error'}`;
    logger.error(errorMsg, error as Error);
    result.errors.push(errorMsg);
  }

  // 2. Sync training plan
  try {
    logger.info('Fetching training plan from backend');
    const response = await apiClient.getStoredTrainingPlan();

    if (response.success && response.data) {
      // Save to localStorage
      localStorage.setItem('pacepilot_training_plan', JSON.stringify(response.data));

      // Update metadata
      const metadata = {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        userProfileHash: '', // Will be updated when plan is regenerated
        version: '1.0.0',
      };
      localStorage.setItem('pacepilot_plan_metadata', JSON.stringify(metadata));

      logger.info('Training plan synced from backend');
      result.planSynced = true;
    } else {
      const errorMsg = `Failed to load plan: ${response.message || response.error}`;
      logger.warn(errorMsg);
      result.errors.push(errorMsg);
    }
  } catch (error) {
    const errorMsg = `Error loading plan: ${error instanceof Error ? error.message : 'Unknown error'}`;
    logger.error(errorMsg, error as Error);
    result.errors.push(errorMsg);
  }

  // Overall success if no errors occurred
  result.success = result.errors.length === 0;

  if (result.success) {
    logger.info('Sync from backend completed successfully');
  } else {
    logger.warn('Sync from backend completed with errors', { errors: result.errors });
  }

  return result;
}
